import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Lock, ShieldCheck, Building2, GraduationCap, User, CalendarDays, Mail, Smartphone } from 'lucide-react';
import type { AcademyInschrijving } from '../../services/medewerkerApi';

/**
 * Inschrijf-wrapper: compacte tussenstap (split-modal) tussen de inkoopknop en
 * de daadwerkelijke activering. Links de vier invoervelden voor het
 * opleidingsinstituut, rechts de gegevensroute: rechtstreeks en versleuteld
 * naar de opleider, nooit naar de werkgever.
 */

type Veld = keyof AcademyInschrijving;

const LEEG: AcademyInschrijving = { voornaam: '', achternaam: '', geboortedatum: '', prive_email: '', mobiel: '' };

function mobielGeldig(v: string): boolean {
  let s = v.replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) s = `+${s.slice(2)}`;
  if (/^06\d{8}$/.test(s)) return true;
  if (/^\+316\d{8}$/.test(s) || /^316\d{8}$/.test(s)) return true;
  return /^\+[1-9]\d{7,14}$/.test(s) && !s.startsWith('+31');
}

function leeftijd(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const nu = new Date();
  let j = nu.getFullYear() - d.getFullYear();
  if (nu.getMonth() < d.getMonth() || (nu.getMonth() === d.getMonth() && nu.getDate() < d.getDate())) j -= 1;
  return j;
}

const Veldrij: React.FC<{ veld: Veld; label: string; hint: string; fout: string | undefined; icon: React.ElementType; children: React.ReactNode }> = ({ veld, label, hint, fout, icon: Icon, children }) => (
  <div>
    <label htmlFor={`inschrijf-${veld}`} className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
      <Icon className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
      {label}
    </label>
    {children}
    {fout ? (
      <p className="mt-1 text-xs text-red-600" role="alert">
        {fout}
      </p>
    ) : (
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    )}
  </div>
);

export const AcademyInschrijfModal: React.FC<{
  titel: string;
  opleider: string | null | undefined;
  knopTekst: string;
  bezig: boolean;
  fout: string | null;
  onSubmit: (gegevens: AcademyInschrijving) => void;
  onClose: () => void;
}> = ({ titel, opleider, knopTekst, bezig, fout, onSubmit, onClose }) => {
  const { t } = useTranslation('medewerker');
  const [g, setG] = useState<AcademyInschrijving>(LEEG);
  const [aangeraakt, setAangeraakt] = useState<Partial<Record<Veld, boolean>>>({});
  const [verzonden, setVerzonden] = useState(false);
  const eersteVeld = useRef<HTMLInputElement>(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    eersteVeld.current?.focus();
    const toets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', toets, true);
    return () => window.removeEventListener('keydown', toets, true);
  }, []);

  const fouten: Partial<Record<Veld, string>> = {};
  if (!g.voornaam.trim()) fouten.voornaam = t('academy.inschrijf.foutVoornaam', { defaultValue: 'Vul je voornaam in.' });
  if (!g.achternaam.trim()) fouten.achternaam = t('academy.inschrijf.foutAchternaam', { defaultValue: 'Vul je achternaam in.' });
  const jaren = leeftijd(g.geboortedatum);
  if (jaren === null || jaren < 16 || jaren > 100) fouten.geboortedatum = t('academy.inschrijf.foutGeboortedatum', { defaultValue: 'Vul een geldige geboortedatum in.' });
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(g.prive_email.trim())) fouten.prive_email = t('academy.inschrijf.foutEmail', { defaultValue: 'Vul een geldig e-mailadres in.' });
  if (!mobielGeldig(g.mobiel)) fouten.mobiel = t('academy.inschrijf.foutMobiel', { defaultValue: 'Vul een geldig mobiel nummer in, bijv. 06 12345678.' });
  const geldig = Object.keys(fouten).length === 0;

  const zet = (veld: Veld) => (e: React.ChangeEvent<HTMLInputElement>) => setG((s) => ({ ...s, [veld]: e.target.value }));
  const blur = (veld: Veld) => () => setAangeraakt((s) => ({ ...s, [veld]: true }));
  const toon = (veld: Veld): string | undefined => ((aangeraakt[veld] || verzonden) ? fouten[veld] : undefined);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setVerzonden(true);
    if (!geldig || bezig) return;
    onSubmit({
      voornaam: g.voornaam.trim(),
      achternaam: g.achternaam.trim(),
      geboortedatum: g.geboortedatum,
      prive_email: g.prive_email.trim(),
      mobiel: g.mobiel.trim(),
    });
  };

  const veldKlasse = (veld: Veld) =>
    `w-full rounded-xl border bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 outline-none transition focus:ring-2 ${
      toon(veld) ? 'border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-emerald-600 focus:ring-emerald-600/20'
    }`;

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-900/40 backdrop-blur-sm" onClick={() => !bezig && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inschrijf-titel"
        className="relative mx-auto my-0 min-h-full w-full max-w-4xl overflow-hidden bg-gray-50 text-gray-700 shadow-2xl ring-1 ring-gray-200 sm:my-12 sm:min-h-0 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={bezig}
          className="absolute right-3 top-3 z-10 rounded-full bg-white p-2 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-100 disabled:opacity-50"
          aria-label={t('academy.inschrijf.sluiten', { defaultValue: 'Sluiten' })}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="grid lg:grid-cols-[1fr_minmax(0,300px)]">
          {/* Links: invoervelden voor de opleider */}
          <form onSubmit={submit} noValidate className="p-6 sm:p-8">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
              <Lock className="h-3 w-3" aria-hidden="true" />
              {t('academy.inschrijf.badge', { defaultValue: 'Inschrijving bij de opleider' })}
            </p>
            <h2 id="inschrijf-titel" className="mt-3 text-xl font-bold text-gray-900 sm:text-2xl">
              {t('academy.inschrijf.titel', { defaultValue: 'Nog even je gegevens voor {{opleider}}', opleider: opleider || t('academy.inschrijf.deOpleider', { defaultValue: 'de opleider' }) })}
            </h2>
            <p className="mt-1.5 text-sm text-gray-500">
              {t('academy.inschrijf.intro', {
                defaultValue: 'Het opleidingsinstituut heeft deze gegevens nodig voor je presentielijst, certificaat en eventuele PE-punten. Je werkgever krijgt ze niet te zien.',
              })}
            </p>
            <p className="mt-1 text-xs text-gray-500">{titel}</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Veldrij veld="voornaam" fout={toon('voornaam')} icon={User} label={t('academy.inschrijf.voornaam', { defaultValue: 'Voornaam' })} hint={t('academy.inschrijf.voornaamHint', { defaultValue: 'Zoals op je certificaat moet komen' })}>
                <input ref={eersteVeld} id="inschrijf-voornaam" type="text" autoComplete="given-name" maxLength={100} value={g.voornaam} onChange={zet('voornaam')} onBlur={blur('voornaam')} className={veldKlasse('voornaam')} aria-invalid={Boolean(toon('voornaam'))} />
              </Veldrij>
              <Veldrij veld="achternaam" fout={toon('achternaam')} icon={User} label={t('academy.inschrijf.achternaam', { defaultValue: 'Achternaam' })} hint={t('academy.inschrijf.achternaamHint', { defaultValue: 'Voor de presentielijst' })}>
                <input id="inschrijf-achternaam" type="text" autoComplete="family-name" maxLength={150} value={g.achternaam} onChange={zet('achternaam')} onBlur={blur('achternaam')} className={veldKlasse('achternaam')} aria-invalid={Boolean(toon('achternaam'))} />
              </Veldrij>
              <Veldrij veld="geboortedatum" fout={toon('geboortedatum')} icon={CalendarDays} label={t('academy.inschrijf.geboortedatum', { defaultValue: 'Geboortedatum' })} hint={t('academy.inschrijf.geboortedatumHint', { defaultValue: 'Wettelijk controleveld voor PE-punten en accreditatieregisters' })}>
                <input id="inschrijf-geboortedatum" type="date" autoComplete="bday" max={new Date().toISOString().slice(0, 10)} value={g.geboortedatum} onChange={zet('geboortedatum')} onBlur={blur('geboortedatum')} className={`${veldKlasse('geboortedatum')}`} aria-invalid={Boolean(toon('geboortedatum'))} />
              </Veldrij>
              <Veldrij veld="mobiel" fout={toon('mobiel')} icon={Smartphone} label={t('academy.inschrijf.mobiel', { defaultValue: 'Mobiel telefoonnummer' })} hint={t('academy.inschrijf.mobielHint', { defaultValue: 'Alleen voor spoedberichten van de opleider (bijv. locatiewijziging of ziekte trainer)' })}>
                <input id="inschrijf-mobiel" type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12345678" maxLength={20} value={g.mobiel} onChange={zet('mobiel')} onBlur={blur('mobiel')} className={veldKlasse('mobiel')} aria-invalid={Boolean(toon('mobiel'))} />
              </Veldrij>
              <div className="sm:col-span-2">
                <Veldrij veld="prive_email" fout={toon('prive_email')} icon={Mail} label={t('academy.inschrijf.email', { defaultValue: 'Privé e-mailadres' })} hint={t('academy.inschrijf.emailHint', { defaultValue: 'Hierop ontvang je inloggegevens voor e-learning of de routebeschrijving. Gebruik liever niet je werkmail.' })}>
                  <input id="inschrijf-prive_email" type="email" inputMode="email" autoComplete="email" maxLength={254} value={g.prive_email} onChange={zet('prive_email')} onBlur={blur('prive_email')} className={veldKlasse('prive_email')} aria-invalid={Boolean(toon('prive_email'))} />
                </Veldrij>
              </div>
            </div>

            {fout && (
              <p className="mt-4 text-sm text-red-600" role="alert">
                {fout}
              </p>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={onClose} disabled={bezig} className="rounded-xl px-4 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                {t('academy.inschrijf.terug', { defaultValue: 'Terug' })}
              </button>
              <button
                type="submit"
                disabled={bezig || (verzonden && !geldig)}
                className="rounded-xl px-5 py-3 text-sm font-bold text-white shadow-lg transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                style={!bezig && (!verzonden || geldig) ? { backgroundColor: '#16a34a', boxShadow: '0 10px 30px -10px rgba(22,163,74,.7)' } : undefined}
              >
                {bezig ? '…' : knopTekst}
              </button>
            </div>
          </form>

          {/* Rechts: de gegevensroute */}
          <aside className="border-t border-gray-200 bg-white p-6 lg:border-l lg:border-t-0">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
              <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              {t('academy.inschrijf.routeTitel', { defaultValue: 'Waar gaan je gegevens heen?' })}
            </h3>
            <ol className="mt-4 space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                  <GraduationCap className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{t('academy.inschrijf.routeOpleider', { defaultValue: 'Versleuteld naar de opleider' })}</p>
                  <p className="mt-0.5 text-gray-500">
                    {t('academy.inschrijf.routeOpleiderUitleg', { defaultValue: 'Je naam, geboortedatum, e-mail en mobiel nummer worden versleuteld opgeslagen en zijn alleen zichtbaar voor het opleidingsinstituut.' })}
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                  <Building2 className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{t('academy.inschrijf.routeWerkgever', { defaultValue: 'Nooit naar je werkgever' })}</p>
                  <p className="mt-0.5 text-gray-500">
                    {t('academy.inschrijf.routeWerkgeverUitleg', { defaultValue: 'Je werkgever ziet alleen dat er een activering plaatsvond op je registratienummer. Deze gegevens staan structureel buiten het werkgeversdashboard.' })}
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                  <Lock className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{t('academy.inschrijf.routeDoel', { defaultValue: 'Alleen voor de opleiding' })}</p>
                  <p className="mt-0.5 text-gray-500">
                    {t('academy.inschrijf.routeDoelUitleg', { defaultValue: 'Presentielijst, certificaat, PE-punten, e-learning-toegang en spoedberichten. Niets anders.' })}
                  </p>
                </div>
              </li>
            </ol>
          </aside>
        </div>
      </div>
    </div>
  );
};
